import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Table } from "../../components";
import FiledCopiesDocuments from "../../components/FiledCopiesDocuments/FiledCopiesDocuments";
import ClientServiceRequestForm from "../../components/ClientServiceRequestForm/ClientServiceRequestForm";
import { CLIENT_ACTION } from "../../redux/action";
import { Toaster, dateTimeFormatter } from "../../utils/common";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import { filterRecordsByClientCategory } from "../../utils/clientDetailsCategory";
import { clientHasRegisteredBusinesses } from "../../utils/clientBusinessAccess";
import { CLIENT_CATEGORY_CHANGE_EVENT } from "../../utils/clientCategoryStorage";
import {
  formatServiceStatus,
  getServiceBusinessName,
  getServiceCategoryLabel,
  SERVICE_DEFAULT_STATUS,
} from "../../constants/clientServiceOptions";
import Empty from "antd/es/empty";
import "./my_services.scss";

const BASE_TABLE_HEADERS = [
  { id: "sno", label: "S.No" },
  { id: "start_date", label: "Start Date" },
  { id: "service_name", label: "Service Name" },
  { id: "tax_year", label: "Tax Year" },
  { id: "service_frequency", label: "Service Frequency" },
  { id: "status", label: "Status" },
  { id: "updated_at", label: "Last Updated Date" },
];

const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem("USER_DATA") || "{}");
  } catch {
    return {};
  }
};

const MyServices = () => {
  const navigate = useNavigate();
  const clientId = localStorage.getItem("user_id");
  const { categoryTab, showCategoryChooser } = useClientCategoryTab();
  const activeCategory = showCategoryChooser ? categoryTab : "individual";
  const categoryLabel = getServiceCategoryLabel(activeCategory);

  const [statusTab, setStatusTab] = useState("ongoing");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [categoryVersion, setCategoryVersion] = useState(0);
  const [clientBusinesses, setClientBusinesses] = useState([]);

  useEffect(() => {
    const onCategoryChange = () => setCategoryVersion((value) => value + 1);
    window.addEventListener(CLIENT_CATEGORY_CHANGE_EVENT, onCategoryChange);
    return () =>
      window.removeEventListener(CLIENT_CATEGORY_CHANGE_EVENT, onCategoryChange);
  }, []);

  useEffect(() => {
    if (activeCategory !== "business") return;
    const userData = readUserData();
    if (!clientHasRegisteredBusinesses(userData)) {
      navigate("/client/select-account", {
        replace: true,
        state: { intent: "business" },
      });
    }
  }, [activeCategory, categoryVersion, navigate]);

  const loadServices = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const data = await CLIENT_ACTION.getClientServices({
        client_id: clientId,
        status: statusTab,
        service_category: activeCategory,
      });
      const list = Array.isArray(data) ? data : [];
      setRows(filterRecordsByClientCategory(list, activeCategory));
    } finally {
      setLoading(false);
    }
  }, [clientId, statusTab, activeCategory, categoryVersion]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    if (!clientId || activeCategory !== "business") {
      setClientBusinesses([]);
      return undefined;
    }
    let cancelled = false;
    CLIENT_ACTION.getClientBusinesses(clientId).then((list) => {
      if (!cancelled) setClientBusinesses(Array.isArray(list) ? list : []);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId, activeCategory, categoryVersion]);

  useEffect(() => {
    setStatusTab("ongoing");
  }, [activeCategory]);

  const tableHeaders = useMemo(() => {
    const headers = [...BASE_TABLE_HEADERS];
    if (activeCategory === "business") {
      headers.splice(3, 0, { id: "business_name", label: "Business" });
    }
    if (statusTab === "closed") {
      const statusIndex = headers.findIndex((header) => header.id === "status");
      headers.splice(statusIndex + 1, 0, {
        id: "filed_copies",
        label: "Filed Copies",
      });
    }
    return headers;
  }, [activeCategory, statusTab]);

  const tableData = useMemo(() => {
    return rows.map((row, index) => ({
      sno: index + 1,
      start_date: (() => {
        const raw = row.start_date || row.startDate;
        if (!raw) return "—";
        const text = String(raw);
        return text.length >= 10 ? text.slice(0, 10) : text;
      })(),
      service_name: row.service_name || row.serviceName || "—",
      ...(activeCategory === "business"
        ? { business_name: getServiceBusinessName(row, clientBusinesses) }
        : {}),
      tax_year: row.tax_year || row.taxYear || "—",
      service_frequency:
        row.service_frequency || row.serviceFrequency || "—",
      status: formatServiceStatus(row.status),
      ...(statusTab === "closed"
        ? {
            filed_copies: (
              <FiledCopiesDocuments
                documents={row.filed_copies || row.filedCopies}
              />
            ),
          }
        : {}),
      updated_at: dateTimeFormatter(
        row.updatedAt || row.updated_at || row.last_updated_at,
      ),
    }));
  }, [rows, clientBusinesses, activeCategory, statusTab]);

  const handleRequestService = async (form) => {
    if (!form?.service_name || !form?.tax_year || !form?.service_frequency) {
      Toaster.warning("Please complete all service fields.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await CLIENT_ACTION.requestClientService({
        client_id: clientId,
        start_date: form.start_date,
        service_category: activeCategory,
        service_name: form.service_name,
        tax_year: form.tax_year,
        service_frequency: form.service_frequency,
        status: SERVICE_DEFAULT_STATUS,
        is_onboarding: false,
        ...(activeCategory === "business"
          ? { client_business_id: form.client_business_id }
          : {}),
      });
      if (res?.status === 1) {
        Toaster.success(res.message || "Service request sent successfully");
        setRequestModalOpen(false);
        setStatusTab("ongoing");
        await loadServices();
      } else {
        Toaster.error(res?.message || "Could not send service request");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="my-services-container">
      <div className="my-services-card">
        <div className="my-services-header">
          <div className="my-services-title-wrapper">
            <h2 className="my-services-title">My Services</h2>
            {showCategoryChooser ? (
              <p className="my-services-category-note">
                Showing <strong>{categoryLabel}</strong> services for your
                account
              </p>
            ) : null}
          </div>
          <div className="my-services-actions">
            <div className="status-toggle">
              <button
                type="button"
                className={`toggle-btn ${statusTab === "ongoing" ? "active" : ""}`}
                onClick={() => setStatusTab("ongoing")}
              >
                Ongoing
              </button>
              <button
                type="button"
                className={`toggle-btn ${statusTab === "closed" ? "active" : ""}`}
                onClick={() => setStatusTab("closed")}
              >
                Closed
              </button>
            </div>
            <Button
              text="Request new service"
              handleClick={() => {
                if (
                  activeCategory === "business" &&
                  !clientHasRegisteredBusinesses(readUserData())
                ) {
                  navigate("/client/select-account", { state: { intent: "business" } });
                  return;
                }
                setRequestModalOpen(true);
              }}
            />
          </div>
        </div>

        {loading ? (
          <p className="my-services-loading">Loading services...</p>
        ) : tableData.length === 0 ? (
          <Empty
            description={`No ${categoryLabel.toLowerCase()} services found`}
          />
        ) : (
          <Table
            variant="small"
            header={tableHeaders}
            data={tableData}
            aligncss="my-services-table"
          />
        )}
      </div>

      {requestModalOpen && (
        <Modal
          title={null}
          open
          handleClose={() => setRequestModalOpen(false)}
          className="my-services-request-modal timesheet-view-modal"
        >
          <div className="my-services-modal-content">
            <div className="my-services-modal-header">
              <h3>Request {categoryLabel.toLowerCase()} service</h3>
              <button
                type="button"
                className="my-services-modal-close"
                onClick={() => setRequestModalOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <ClientServiceRequestForm
              defaultCategory={activeCategory}
              lockCategory
              clientId={clientId}
              businesses={clientBusinesses}
              submitting={submitting}
              onSubmit={handleRequestService}
              onCancel={() => setRequestModalOpen(false)}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default MyServices;
